/**
 * Player settings.
 *
 * The defaults are the product's argument in miniature: no mistake checking, no
 * auto-candidates, timer visible but stoppable. Every assist is available and
 * every assist is off until asked for, so the purist gets a purist's grid and
 * the learner is one toggle away from help.
 */

export type InputMode = "cell-first" | "digit-first";
export type MarkKind = "corner" | "center";
export type ThemeChoice = "system" | "light" | "dark";

export interface Settings {
  inputMode: InputMode;
  /** Which note row a digit press writes to while notes mode is on. */
  markKind: MarkKind;
  /** Show the true candidates in every empty cell. */
  autoCandidates: boolean;
  /** Strip a placed digit from its peers' notes automatically. */
  cleanPeerMarks: boolean;
  /** Flag digits that disagree with the solution. Off by default. */
  checkMistakes: boolean;
  /** Tint the selected cell's row, column and box. */
  highlightPeers: boolean;
  /** Tint every cell holding the selected digit. */
  highlightSameDigit: boolean;
  showTimer: boolean;
  theme: ThemeChoice;
}

export const DEFAULT_SETTINGS: Settings = {
  inputMode: "cell-first",
  markKind: "center",
  autoCandidates: false,
  cleanPeerMarks: true,
  checkMistakes: false,
  highlightPeers: true,
  highlightSameDigit: true,
  showTimer: true,
  theme: "system",
};

const KEY = "sudoku.settings.v1";

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    // Merge rather than trust: a stored blob from an older build must not be
    // able to leave a new setting undefined.
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Private browsing, or storage full. Settings are a convenience, not state
    // worth failing a game over.
  }
}
